/**
 * Aufgaben — Automatisch generierte und manuelle Aufgaben-Übersicht.
 *
 * Features:
 * - Filterung nach Status (offen/erledigt/ignoriert) und Priorität
 * - Prioritäts-Badges (kritisch, wichtig, info)
 * - Aktionen: Erledigen, Ignorieren, Navigation zum Kontext
 * - Zähler-KPIs nach Priorität
 *
 * @page /aufgaben
 * @roles admin, manager
 * @i18n tasks, common
 * @endpoints GET /api/tasks, GET /api/tasks/count, POST /api/tasks/:id/erledigt, POST /api/tasks/:id/ignorieren
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader, Badge, Button } from '../components/ui';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';

const PRIO_STYLES = {
  kritisch: { dot: 'bg-red-500', labelKey: 'tasks.critical', color: 'text-red-500' },
  wichtig:  { dot: 'bg-amber-500', labelKey: 'tasks.important', color: 'text-amber-500' },
  info:     { dot: 'bg-[var(--text-tertiary)]', labelKey: 'tasks.info', color: 'text-[var(--text-tertiary)]' },
};

function formatDeadline(d, t) {
  if (!d) return null;
  const date = new Date(d);
  const now = new Date();
  const diff = Math.floor((date - now) / (1000 * 60 * 60 * 24));
  if (diff < 0) return t('tasks.overdue', { days: Math.abs(diff) });
  if (diff === 0) return t('tasks.dueToday');
  if (diff === 1) return t('tasks.dueTomorrow');
  return date.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function TaskRow({ task, onErledigt, onIgnorieren, onNavigate, t }) {
  const prio = PRIO_STYLES[task.prioritaet] || PRIO_STYLES.info;
  return (
    <div className="group flex items-start gap-3 py-3 px-4 hover:bg-[var(--bg-card-hover)] transition-colors border-b border-[var(--border-subtle)] last:border-b-0">
      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${prio.dot}`} />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-[var(--text-primary)] leading-tight">
          {task.titel}
        </div>
        {task.beschreibung && (
          <div className="text-[11px] text-[var(--text-tertiary)] mt-1 line-clamp-2">{task.beschreibung}</div>
        )}
        <div className="flex items-center gap-2 mt-1.5">
          <Badge variant="default">{task.kategorie}</Badge>
          {task.deadline && (
            <span className="text-[11px] text-[var(--text-tertiary)]">
              {t('tasks.deadline')}: {formatDeadline(task.deadline, t)}
            </span>
          )}
          <span className="text-[10px] text-[var(--text-tertiary)]">
            {new Date(task.erstellt_am).toLocaleDateString('de-AT')}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {task.link && (
          <Button variant="ghost" size="xs" onClick={() => onNavigate(task.link)}>{t('tasks.open')}</Button>
        )}
        <Button variant="ghost" size="xs" onClick={() => onErledigt(task.id)}>{t('tasks.done')}</Button>
        <Button variant="ghost" size="xs" onClick={() => onIgnorieren(task.id)}>{t('tasks.ignore')}</Button>
      </div>
    </div>
  );
}

export default function Aufgaben() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({ total: 0, kritisch: 0, wichtig: 0, info: 0 });
  const [loading, setLoading] = useState(true);
  const [showErledigt, setShowErledigt] = useState(false);

  const filterPrio = searchParams.get('prioritaet') || '';
  const filterKat = searchParams.get('kategorie') || '';

  const fetchTasks = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterPrio) params.set('prioritaet', filterPrio);
      if (filterKat) params.set('kategorie', filterKat);
      const [tasksRes, countRes] = await Promise.all([
        fetch(`${API}/api/tasks?${params}`, { credentials: 'include' }).then(r => r.ok ? r.json() : { tasks: [] }),
        fetch(`${API}/api/tasks/count`, { credentials: 'include' }).then(r => r.ok ? r.json() : { total: 0, kritisch: 0, wichtig: 0, info: 0 }),
      ]);
      setTasks(tasksRes.tasks || []);
      setCounts(countRes);
    } catch { /* silent */ }
    setLoading(false);
  }, [filterPrio, filterKat]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const handleErledigt = async (id) => {
    await fetch(`${API}/api/tasks/${id}/erledigt`, { method: 'POST', credentials: 'include' });
    fetchTasks();
  };

  const handleIgnorieren = async (id) => {
    await fetch(`${API}/api/tasks/${id}/ignorieren`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tage: 30 }),
    });
    fetchTasks();
  };

  const setFilter = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    setSearchParams(p);
  };

  // Group by priority
  const grouped = { kritisch: [], wichtig: [], info: [] };
  for (const task of tasks) {
    (grouped[task.prioritaet] || grouped.info).push(task);
  }

  // Get unique categories
  const categories = [...new Set(tasks.map(task => task.kategorie))].sort();

  const prioLabels = {
    kritisch: t('tasks.critical'),
    wichtig: t('tasks.important'),
    info: t('tasks.info'),
  };

  return (
    <div className="p-page">
      <PageHeader
        title={t('tasks.title')}
        subtitle={t('tasks.openCount', { n: counts.total })}
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-1 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-card p-1">
          {[
            { key: '', label: t('common.all') },
            { key: 'kritisch', label: t('tasks.critical'), count: counts.kritisch, dot: 'bg-red-500' },
            { key: 'wichtig', label: t('tasks.important'), count: counts.wichtig, dot: 'bg-amber-500' },
            { key: 'info', label: t('tasks.info'), count: counts.info, dot: 'bg-[var(--text-tertiary)]' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter('prioritaet', f.key)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-button text-[11px] font-medium transition-colors ${
                filterPrio === f.key
                  ? 'bg-[var(--bg-active)] text-[var(--text-primary)]'
                  : 'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]'
              }`}
            >
              {f.dot && <span className={`w-1.5 h-1.5 rounded-full ${f.dot}`} />}
              {f.label}{f.count != null ? ` (${f.count})` : ''}
            </button>
          ))}
        </div>

        {categories.length > 1 && (
          <select
            value={filterKat}
            onChange={e => setFilter('kategorie', e.target.value)}
            className="bg-[var(--bg-input)] border border-[var(--border-input)] rounded-input text-[11px] text-[var(--text-secondary)] px-2 py-1"
          >
            <option value="">{t('tasks.allCategories')}</option>
            {categories.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        )}
      </div>

      {/* Task Groups */}
      {loading ? (
        <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-card p-8 text-center">
          <div className="animate-spin h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full mx-auto" />
        </div>
      ) : tasks.length === 0 ? (
        <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-card p-8 text-center text-xs text-[var(--text-tertiary)]">
          {t('tasks.noOpenTasks')}
        </div>
      ) : (
        <div className="space-y-3">
          {['kritisch', 'wichtig', 'info'].map(prio => {
            const items = grouped[prio];
            if (items.length === 0) return null;
            const style = PRIO_STYLES[prio];
            return (
              <div key={prio} className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-card overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2 border-b border-[var(--border-subtle)]" style={{ background: 'var(--table-header)' }}>
                  <span className={`w-2 h-2 rounded-full ${style.dot}`} />
                  <span className="text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wider">
                    {prioLabels[prio]} ({items.length})
                  </span>
                </div>
                {items.map(task => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    onErledigt={handleErledigt}
                    onIgnorieren={handleIgnorieren}
                    onNavigate={(link) => navigate(link)}
                    t={t}
                  />
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
